import { ref, inject, computed, unref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import shuffle from 'lodash/shuffle';
import debounce from 'lodash/debounce';
import ProfileLinkWrapper from '../component-lib.esm4.js';
import CarouselItem from './component-lib.esm3.js';
import ChevronIcon from '../../icons/arrows/component-lib.esm2.js';
import { useLink } from '../component-lib.esm6.js';
import { LINK_PROPS } from '../component-lib.esm3.js';
import IconButton from '../../components/icon-button/component-lib.esm.js';
import { useBtnColors } from '../compositions/component-lib.esm.js';
import { useResizeObserver } from '../../compositions/component-lib.esm.js';
import { isClient } from '../../utils/component-lib.esm5.js';

//
var script = {
  props: LINK_PROPS,
  components: {
    CarouselItem,
    ProfileLinkWrapper,
    ChevronIcon,
    IconButton,
    GestureSwipeIcon: () => import('../../icons/component-lib.esm85.js').then(d => d.default)
  },
  setup(props, _ref) {
    let {
      emit
    } = _ref;
    const nav = ref(null);
    const navHeight = ref('0px');
    const swipeWrapper = ref(null);
    const playSwipeAnimation = ref(false);
    const index = ref(0);
    const dragThreshold = ref(50);
    const movePercent = ref(0.2);
    const distanceBetweenBanners = ref(8);
    const bannerWidth = ref(0);
    const noTransition = ref(false);
    const translateX = ref('');
    const isDragging = ref(false);
    const showMovement = ref(false);
    const hasMoved = ref(false);
    const preventClick = ref(false);
    const moveX = ref(null);
    const startX = ref(null);
    const startY = ref(null);
    const hasInteracted = ref(false);
    const carousel = ref(null);
    const {
      isInAdmin = false
    } = inject('app', {});
    const {
      id,
      enableUtmParameters,
      utmMedium,
      utmSource
    } = useLink(props, {
      emit
    });
    const profile = inject('profile');
    const bannerOptions = computed(() => {
      return props.link.carousel_options;
    });
    const sliderClasses = computed(() => {
      return {
        'no-trans': noTransition.value,
        'site-carousel--has-interacted': hasInteracted.value
      };
    });
    const bannerMedia = computed(() => {
      var _props$link$custom_ba;
      return (_props$link$custom_ba = props.link.custom_banner_media) === null || _props$link$custom_ba === void 0 ? void 0 : _props$link$custom_ba.filter(media => media);
    });
    const banners = computed(() => {
      const banners = [...props.link.banners]
      // remove banners without media and not enabled
      .filter(banner => banner.media && banner.enabled)
      // map the media item into the banner object
      .map(banner => {
        var _bannerMedia$value;
        if ((_bannerMedia$value = bannerMedia.value) !== null && _bannerMedia$value !== void 0 && _bannerMedia$value.length) {
          banner.media_item = bannerMedia.value.find(media => media._id.$oid === banner.media.$oid);
        }
        if (!banner.media_item) {
          banner.media_item = {
            url: banner.media_url
          };
        }
        return banner;
      })
      // sort the banners by position
      .sort((a, b) => a.position - b.position);
      return bannerOptions.value.is_carousel_random ? shuffle(banners) : banners;
    });
    const numberOfBanners = computed(() => {
      return banners.value.length;
    });
    const bannerWidthPercent = computed(() => {
      return 100 / numberOfBanners.value;
    });
    const sliderStyles = computed(() => {
      return {
        width: `calc(${numberOfBanners.value * 100}% + ${numberOfBanners.value * distanceBetweenBanners.value}px)`,
        translateX: translateX.value,
        WebkitTransform: translateX.value
      };
    });
    const maxX = computed(() => {
      return bannerWidth.value * (numberOfBanners.value - 1);
    });
    const classes = computed(() => {
      const {
        site_carousel_arrows_style,
        site_carousel_dots_style
      } = unref(profile);
      return {
        [`site-carousel--arrows-${site_carousel_arrows_style}`]: site_carousel_arrows_style,
        [`site-carousel--dots-${site_carousel_dots_style}`]: site_carousel_dots_style
      };
    });
    const {
      backgroundColor: btnBgColor,
      color: btnColor
    } = useBtnColors();
    const styles = computed(() => {
      return {
        '--c-nav-height': navHeight.value,
        '--c-nav-bg-color': btnBgColor.value,
        '--c-nav-color': btnColor.value,
        '--c-height-ratio': `${bannerOptions.value.height_ratio}%`
      };
    });
    function handleNavClick(index) {
      goTo(index);
    }
    useResizeObserver(nav, entries => {
      navHeight.value = `${entries[0].contentRect.height}px`;
    });
    function handleInView() {
      if (numberOfBanners.value > 1) {
        playSwipeAnimation.value = true;
      }
    }
    function hideSwipeIcon() {
      // animate to opacity 0
      swipeWrapper.value.animate([{
        opacity: 1
      }, {
        opacity: 0
      }], {
        duration: 250,
        iterations: 1
      }).onfinish = () => {
        playSwipeAnimation.value = false;
      };
    }
    async function reset() {
      await nextTick();
      index.value = 0;
      updateBannerWidth();
      handleBannerFocus(index.value);
    }
    const resetDebounced = debounce(reset, 1000);
    watch(() => props.link, () => {
      resetDebounced();
    });
    function handleWrapperEvent(e) {
      // Send first banner id with wrapper events
      emit('event', {
        name: e,
        banner_id: banners.value[0]._id.$oid,
        position: 1
      });
    }
    function updateBannerWidth() {
      bannerWidth.value = carousel.value.children[0].offsetWidth;
    }
    function addListeners() {
      carousel.value.addEventListener('mousemove', touchUpdate, {
        passive: true
      });
      carousel.value.addEventListener('mouseup', touchCancel, {
        passive: true
      });
      carousel.value.addEventListener('mouseleave', touchCancel);
      carousel.value.addEventListener('touchmove', touchUpdate, {
        passive: true
      });
      carousel.value.addEventListener('touchend', touchCancel, {
        passive: true
      });
      carousel.value.addEventListener('touchcancel', touchCancel, {
        passive: true
      });
    }
    function removeListeners() {
      if (isClient) {
        carousel.value.removeEventListener('mousemove', touchUpdate, {
          passive: true
        });
        carousel.value.removeEventListener('mouseup', touchCancel, {
          passive: true
        });
        carousel.value.removeEventListener('mouseleave', touchCancel);
        carousel.value.removeEventListener('touchmove', touchUpdate, {
          passive: true
        });
        carousel.value.removeEventListener('touchend', touchCancel, {
          passive: true
        });
        carousel.value.removeEventListener('touchcancel', touchCancel, {
          passive: true
        });
      }
    }
    function touchStart(e) {
      // don't swipe if using more than one finger
      if (e.touches && e.touches.length > 1) return;
      noTransition.value = true;
      isDragging.value = true;
      hasInteracted.value = true;
      startX.value = e.touches ? e.touches[0].clientX : e.pageX;
      startY.value = e.touches ? e.touches[0].clientY : e.pageY;
      addListeners();
    }
    function touchUpdate(e) {
      const y = e.touches ? e.touches[0].clientY : e.pageY;
      // only start moving carousel if actually dragging
      if (isDragging.value && Math.abs(startY.value - y) < dragThreshold.value) {
        e.preventDefault();
        hasMoved.value = true;
        const x = e.touches ? e.touches[0].clientX : e.pageX;
        moveX.value = -(index.value * bannerWidth.value + (startX.value - x));

        // prevent the carousel from moving outside it's bounds
        if (moveX.value > 0) {
          moveX.value = 0;
        } else if (Math.abs(moveX.value) > maxX.value) {
          moveX.value = -maxX.value;
        }
        updateTransform(moveX.value / carousel.value.clientWidth * 100);
      }
    }
    function touchCancel(e) {
      if (isDragging.value) {
        noTransition.value = false;
        isDragging.value = false;

        // make sure the user has actually swiped
        if (hasMoved.value) {
          hasMoved.value = false;
          // console.log('touch end', e);
          // see if we should prevent a click event
          const y = e.changedTouches ? e.changedTouches[0].clientY : e.pageY,
            x = e.changedTouches ? e.changedTouches[0].clientX : e.pageX;
          if (Math.abs(startY.value - y) < dragThreshold.value && Math.abs(startX.value - x) > dragThreshold.value / 2) {
            preventClick.value = true;
          }
          const moved = index.value * bannerWidth.value - -moveX.value;
          let newIndex = index.value;
          if (Math.abs(moved) > bannerWidth.value * movePercent.value) {
            if (moved < 0) newIndex = index.value + 1;else newIndex = index.value - 1;
          }
          goTo(newIndex, false);
        }
      }
      removeListeners();
    }
    function updateTransform(x) {
      translateX.value = `translateX(${x}%)`;
    }
    async function goTo(i) {
      let setPrevent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      if (setPrevent) preventClick.value = false;
      hasInteracted.value = true;
      await nextTick();
      carousel.value.querySelector(`#carousel-${id.value}-${i}`).focus({
        preventScroll: true
      });
      // Reset scroll position
      window.scrollTo(0, window.scrollY);
    }
    async function handleBannerFocus(i) {
      index.value = i;
      await nextTick();
      updateTransform(-(i * 100 / numberOfBanners.value));
    }
    function handleBannerClick(e, banner) {
      if (preventClick.value) {
        e.preventDefault();
        preventClick.value = false;
      } else {
        emit('click', {
          banner_id: banner._id.$oid,
          href: banner.url,
          text: banner.image_alt
        });
      }
    }
    function handleImageLoaded() {
      if (!showMovement.value && numberOfBanners.value > 1) {
        showMovement.value = true;
      }
    }
    useResizeObserver(carousel, updateBannerWidth);
    onMounted(() => {
      carousel.value.addEventListener('mousedown', touchStart, {
        passive: true
      });
      carousel.value.addEventListener('touchstart', touchStart, {
        passive: true
      });
      updateBannerWidth();
    });
    onBeforeUnmount(() => {
      if (isClient) {
        carousel.value.removeEventListener('mousedown', touchStart, {
          passive: true
        });
        carousel.value.removeEventListener('touchstart', touchStart, {
          passive: true
        });
        removeListeners();
      }
    });
    return {
      index,
      carousel,
      id,
      handleNavClick,
      nav,
      navHeight,
      bannerOptions,
      classes,
      styles,
      btnBgColor,
      btnColor,
      profile,
      swipeWrapper,
      playSwipeAnimation,
      handleInView,
      hideSwipeIcon,
      isInAdmin,
      enableUtmParameters,
      utmMedium,
      utmSource,
      sliderClasses,
      sliderStyles,
      banners,
      numberOfBanners,
      bannerWidthPercent,
      handleWrapperEvent,
      handleImageLoaded,
      handleBannerClick,
      handleBannerFocus,
      goTo
    };
  }
};
export { script as default };